// @ts-nocheck
import { formGlobals } from './_/form-globals';
import BaseInput from './_/base-input';
import AirDatepicker from 'air-datepicker';

/**
 * Class: DateInput
 * Purpose: Manage a single <input type="date">, validate dates (required, min/max/step),
 *          support data-set-min-today="true", compose aria-describedby (note/error), and emit events.
 * Constructor Parameters (types):
 *   wrap (HTMLElement)      - Container element that wraps the input + error UI.
 *   formIndex (number|null) - Index of the parent form instance.
 *   wrapIndex (number|null) - Index of this field within the form instance.
 *
 * Public Methods:
 *   isValid(): boolean
 *     // Returns current validity state.
 *
 *   getState(): { value:string, dateValue:Date|null, valid:boolean, blurred:boolean, required:boolean, id:string, iso:string|null, min:string|null, max:string|null }
 *     // Snapshot incl. parsed Date (local) and ISO yyyy-mm-dd (or null) and effective min/max.
 *
 *   validateNow(): void
 *     // Forces validation against current value.
 *
 *   destroy(): void
 *     // Removes listeners.
 *
 * Events:
 *   'validchange' (detail:{ valid:boolean, value:string, dateValue:Date|null, instance:DateInput })
 *   'valuechange' (detail:{ value:string, dateValue:Date|null, instance:DateInput })
 *   'blur'        (detail:{ instance:DateInput })
 *   'focus'       (detail:{ instance:DateInput })
 * 
 * @class
 * @extends {BaseInput}
 * @this {DateInput}
 */
export default class DateInput extends BaseInput {
    /**
     * @param {HTMLElement} wrap
     * @param {number|null} formIndex
     * @param {number|null} wrapIndex
     */
    constructor(wrap, formIndex = null, wrapIndex = null) {
      super(wrap, formIndex, wrapIndex);
  
      /** @type {HTMLInputElement|null} */
      this.input = wrap.querySelector('input[type="text"][data-datepicker="true"]');
      // Ensure formIndex and wrapIndex are numbers, not null
      const safeFormIndex = formIndex ?? 0;
      const safeWrapIndex = wrapIndex ?? 0;
      this.inputId = `form-${safeFormIndex}-input-${safeWrapIndex}`;
  
      // Initialize base class elements
      this._initErrorElement();
      this._initNoteElement(this.wrap.querySelector(formGlobals.noteSelector) || null);
  
      // config
      this.required    = this.input?.getAttribute('aria-required') === 'true';
      this.validateKey = this.input?.getAttribute('data-validate') || null;
  
      // Native attributes (raw)
      this.minAttr  = this.input?.getAttribute('min') || null;   // "YYYY-MM-DD"
      this.maxAttr  = this.input?.getAttribute('max') || null;
      this.stepAttr = this.input?.getAttribute('step') || null;  // integer days | "any"
  
      // Only treat as true if explicitly "true"
      this.setMinToday = this.input?.getAttribute('data-set-min-today') === 'true';
  
      // compute effective min (and reflect to DOM if needed)
      this.effectiveMin = this._computeEffectiveMin();

      // Detect locale from data-locale attribute, html lang attribute, or default to 'de'
      const localeAttr = this.input?.getAttribute('data-locale') || 
                        this.wrap.getAttribute('data-locale') ||
                        document.documentElement.lang?.toLowerCase() ||
                        'de';
      this.locale = localeAttr.startsWith('en') ? 'en' : 'de';

      // state
      this.blurred = false;
      this.value = this.input?.value ?? '';
      // Try to parse as locale-specific format first, then fall back to ISO
      this.dateValue = DateInput.parseLocaleDate(this.value, this.locale) || 
                       DateInput.parseLocalDate(this.value);
      this.valid = this.required ? false : true;

      // AirDatepicker instance
      this.datepicker = null;

      // listeners cleanup
      this._onFocus = this._onFocus.bind(this);
      this._onBlur  = this._onBlur.bind(this);
      this._onInput = this._onInput.bind(this);

      this._setAttributes();
      this._initEvents();
      this._initDatepicker();
    }

    /** @returns {HTMLInputElement|null} */
    _getAriaElement() {
      return this.input;
    }

    _getValidChangeDetail(): { valid: boolean; value: string; dateValue: Date | null; instance: DateInput } {
      return {
        valid: this.valid,
        value: this.value,
        dateValue: this.dateValue,
        instance: this
      };
    }
  
    /** @returns {boolean} */
    isValid() { return this.valid; }
  
    /** @returns {{value:string, dateValue:Date|null, valid:boolean, blurred:boolean, required:boolean, id:string, iso:string|null, min:string|null, max:string|null}} */
    getState() {
      return {
        value: this.value,
        dateValue: this.dateValue,
        valid: this.valid,
        blurred: this.blurred,
        required: this.required,
        id: this.inputId,
        iso: this.dateValue ? DateInput.toISO(this.dateValue) : null,
        min: this.effectiveMin,
        max: this.maxAttr
      };
    }
  
    /** Force validation against current value. */
    validateNow() { this._validate(this.value); }
  
    /** Remove listeners and references. */
    destroy() {
      if (this.datepicker) {
        this.datepicker.destroy();
        this.datepicker = null;
      }
      super.destroy();
      this.input = null;
    }
  
    // ---------- Private ----------
    /**
     * Function: _computeEffectiveMin
     * Purpose: Determine effective min based on data-set-min-today="true" and existing min.
     * Behavior:
     *   - If setMinToday=false  → return existing minAttr as-is (no DOM change).
     *   - If setMinToday=true   → effective = laterOf(today, minAttr?); write to DOM (min=effective).
     * Returns: string|null (ISO yyyy-mm-dd)
     */
    _computeEffectiveMin() {
      const attrMin = this.minAttr;
      if (!this.setMinToday) return attrMin;
  
      const todayISO = DateInput.todayISO();
      let effective = todayISO;
  
      if (attrMin) {
        const a = DateInput.parseLocalDate(attrMin);
        const t = DateInput.parseLocalDate(todayISO);
        if (a && t && DateInput.cmp(a, t) > 0) {
          effective = attrMin; // keep later existing min
        }
      }
  
      if (this.input) this.input.setAttribute('min', effective);
      return effective;
    }
  
    /**
     * Function: _setAttributes
     * Purpose: Initialize IDs and initial aria-describedby based on note/error presence.
     */
    _setAttributes() {
      if (!this.input) return;
  
      this.input.setAttribute('id', this.inputId);
  
      // Initial describedby: note if present
      this._setAriaDescribedBy({ includeNote: true, includeError: false });
    }
  
    /**
     * Function: _initDatepicker
     * Purpose: Initialize AirDatepicker instance for the date input.
     */
    _initDatepicker() {
      if (!this.input) return;

      // Get locale configuration
      const localeConfig = DateInput.getLocaleConfig(this.locale);

      const datepickerOptions: any = {
        onSelect: ({ date, formattedDate, datepicker }) => {
          if (date instanceof Date) {
            // Format date according to locale
            const formattedDate = DateInput.formatDateByLocale(date, this.locale);
            this.input.value = formattedDate;
            this.value = formattedDate;
            this.dateValue = date;
            
            // Trigger input event to update filled class and validation
            this._onInput();
          }
        },
        onShow: () => {
          // Ensure input is focused
          if (document.activeElement !== this.input) {
            this.input.focus();
          }
        },
        onHide: () => {
          // Trigger blur if input loses focus
          if (document.activeElement !== this.input) {
            this._onBlur();
          }
        },
        // Set initial date if value exists
        selectedDates: this.dateValue ? [this.dateValue] : [],
        // Localization
        locale: localeConfig
      };

      // Only set minDate/maxDate if they have valid values
      if (this.effectiveMin) {
        const minDate = new Date(this.effectiveMin);
        if (!isNaN(minDate.getTime())) {
          datepickerOptions.minDate = minDate;
        }
      }
      
      if (this.maxAttr) {
        const maxDate = new Date(this.maxAttr);
        if (!isNaN(maxDate.getTime())) {
          datepickerOptions.maxDate = maxDate;
        }
      }

      this.datepicker = new AirDatepicker(this.input, datepickerOptions);
    }

    /**
     * Function: _initEvents
     * Purpose: Attach DOM listeners with AbortController for clean teardown.
     */
    _initEvents() {
      if (!this.input) return;
      const o = { signal: this._ac.signal };

      this.input.addEventListener('focus', this._onFocus, o);
      this.input.addEventListener('blur',  this._onBlur,  o);
      this.input.addEventListener('input', this._onInput, o);
      this.input.addEventListener('change', this._onInput, o); // some browsers mainly fire change
      
      // Also show datepicker on click
      this.input.addEventListener('click', this._onFocus, o);
    }
  
    _onFocus() {
      this.wrap.classList.add(formGlobals.focusedClass);
      this._dispatch('focus', { instance: this });
      
      // Show datepicker if not already shown
      if (this.datepicker && !this.datepicker.visible) {
        this.datepicker.show();
      }
    }
  
    _onBlur() {
      this.wrap.classList.remove(formGlobals.focusedClass);
      const wasFirst = !this.blurred;
      this.blurred = true;
      this._dispatch('blur', { instance: this });
  
      if (wasFirst && this.required) {
        this._validate(this.value);
      }
    }
  
    _onInput() {
      this.value = this.input.value;
      // Try to parse as locale-specific format first, then fall back to ISO
      this.dateValue = DateInput.parseLocaleDate(this.value, this.locale) || 
                       DateInput.parseLocalDate(this.value);
  
      if (this.value) {
        this.wrap.classList.add(formGlobals.filledClass);
      } else {
        this.wrap.classList.remove(formGlobals.filledClass);
      }
  
      this._dispatch('valuechange', { value: this.value, dateValue: this.dateValue, instance: this });
  
      if (this.blurred && (this.required || this.value !== '')) {
        this._validate(this.value);
      }
    }
  
    /**
     * Function: _validate
     * Purpose: Apply required rule, effective min/max/step, and optional data-validate rules.
     */
    _validate(raw) {
      const v = raw.trim();
      // Try to parse as locale-specific format first, then fall back to ISO
      const d = DateInput.parseLocaleDate(v, this.locale) || DateInput.parseLocalDate(v);
  
      // Required rule
      if (this.required) {
        if (v === '' || !d) return this._setInvalid();
      } else {
        if (v === '') return this._setValid();
        if (!d) return this._setInvalid();
      }
  
      // min (effective: either existing min, or today if setMinToday=true)
      if (this.effectiveMin) {
        const minD = DateInput.parseLocalDate(this.effectiveMin);
        if (minD && DateInput.cmp(d, minD) < 0) return this._setInvalid();
      }
  
      // max
      if (this.maxAttr) {
        const maxD = DateInput.parseLocalDate(this.maxAttr);
        if (maxD && DateInput.cmp(d, maxD) > 0) return this._setInvalid();
      }
  
      // step (days)
      if (this.stepAttr && this.stepAttr !== 'any') {
        const stepDays = parseInt(this.stepAttr, 10);
        if (Number.isFinite(stepDays) && stepDays > 0) {
          const base = this.effectiveMin
            ? DateInput.parseLocalDate(this.effectiveMin)
            : DateInput.EPOCH_LOCAL;
          const diffDays = DateInput.diffDays(base, d);
          if (!Number.isFinite(diffDays)) return this._setInvalid();
          if (Math.round(diffDays) !== diffDays) return this._setInvalid(); // ensure integer day
          if ((diffDays % stepDays) !== 0) return this._setInvalid();
        }
      }
  
      // optional data-validate rules (same Syntax wie zuvor)
      if (this.validateKey) {
        const rules = DateInput.parseValidateKey(this.validateKey);
        for (const rule of rules) {
          if (!DateInput.applyRule(rule, d)) return this._setInvalid();
        }
      }
  
      return this._setValid();
    }
  
  
    // ---------- Static helpers & rules ----------
    /**
     * Get locale configuration for AirDatepicker
     * @param {string} locale - 'de' or 'en'
     * @returns {object} Locale configuration object
     */
    static getLocaleConfig(locale) {
      if (locale === 'en') {
        return {
          days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          daysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          today: 'Today',
          clear: 'Clear',
          dateFormat: 'MM/dd/yyyy', // Month/Day/Year format
          timeFormat: 'HH:mm',
          firstDay: 0 // Sunday
        };
      } else {
        // Default: German
        return {
          days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
          daysShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
          daysMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
          months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
          monthsShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
          today: 'Heute',
          clear: 'Löschen',
          dateFormat: 'dd.MM.yyyy', // Tag.Monat.Jahr format
          timeFormat: 'HH:mm',
          firstDay: 1 // Monday
        };
      }
    }

    /**
     * Format date according to locale
     * @param {Date} date - Date object to format
     * @param {string} locale - 'de' or 'en'
     * @returns {string} Formatted date string
     */
    static formatDateByLocale(date, locale) {
      if (locale === 'en') {
        // Format: MM/DD/YYYY (Month/Day/Year)
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      } else {
        // Format: DD.MM.YYYY (Tag.Monat.Jahr)
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      }
    }

    /**
     * Parse date from locale-specific format
     * @param {string} dateString - Date string in locale format
     * @param {string} locale - 'de' or 'en'
     * @returns {Date|null} Parsed date or null if invalid
     */
    static parseLocaleDate(dateString, locale) {
      if (!dateString || typeof dateString !== 'string') return null;
      
      if (locale === 'en') {
        // Parse MM/DD/YYYY format
        const match = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!match) return null;
        const [, month, day, year] = match;
        const date = new Date(+year, +month - 1, +day);
        if (date.getFullYear() === +year && date.getMonth() === +month - 1 && date.getDate() === +day) {
          return date;
        }
      } else {
        // Parse DD.MM.YYYY format
        const match = dateString.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
        if (!match) return null;
        const [, day, month, year] = match;
        const date = new Date(+year, +month - 1, +day);
        if (date.getFullYear() === +year && date.getMonth() === +month - 1 && date.getDate() === +day) {
          return date;
        }
      }
      
      return null;
    }

    static parseLocalDate(iso) {
      if (!iso || typeof iso !== 'string') return null;
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return null;
      const y = +m[1], mo = +m[2] - 1, d = +m[3];
      const date = new Date(y, mo, d); // local midnight
      return (date.getFullYear() === y && date.getMonth() === mo && date.getDate() === d) ? date : null;
    }
  
    static toISO(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  
    static todayISO() {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return DateInput.toISO(today);
    }
  
    static cmp(a, b) {
      const ax = a.getFullYear()*10000 + (a.getMonth()+1)*100 + a.getDate();
      const bx = b.getFullYear()*10000 + (b.getMonth()+1)*100 + b.getDate();
      return ax < bx ? -1 : ax > bx ? 1 : 0;
    }
  
    static diffDays(a, b) {
      const MS = 24 * 60 * 60 * 1000;
      const ax = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
      const bx = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
      return Math.round((bx - ax) / MS);
    }
  
    static EPOCH_LOCAL = new Date(1970, 0, 1);
  
    static parseValidateKey(key) {
      const parts = key.split(/[,\s]+/).filter(Boolean);
      return parts.map(p => {
        const [t, argStr] = p.split(':');
        const list = argStr ? argStr.split(',').map(s => s.trim()) : [];
        return { type: t, list };
      });
    }
  
    static applyRule(rule, d) {
      const name = rule.type;
      switch (name) {
        case 'notPast': {
          const t = DateInput.parseLocalDate(DateInput.todayISO());
          return DateInput.cmp(d, t) >= 0;
        }
        case 'notFuture': {
          const t = DateInput.parseLocalDate(DateInput.todayISO());
          return DateInput.cmp(d, t) <= 0;
        }
        case 'before': {
          const x = rule.list[0] && DateInput.parseLocalDate(rule.list[0]);
          return x ? DateInput.cmp(d, x) < 0 : true;
        }
        case 'after': {
          const x = rule.list[0] && DateInput.parseLocalDate(rule.list[0]);
          return x ? DateInput.cmp(d, x) > 0 : true;
        }
        case 'range': {
          const a = rule.list[0] && DateInput.parseLocalDate(rule.list[0]);
          const b = rule.list[1] && DateInput.parseLocalDate(rule.list[1]);
          if (a && b) return DateInput.cmp(d, a) >= 0 && DateInput.cmp(d, b) <= 0;
          return true;
        }
        case 'weekdaysWhitelist': {
          const set = DateInput._weekdaySet(rule.list);
          return set.has(d.getDay());
        }
        case 'weekdaysBlacklist': {
          const set = DateInput._weekdaySet(rule.list);
          return !set.has(d.getDay());
        }
        default:
          return true;
      }
    }
  
    static _weekdaySet(list) {
      const map = { sun:0, mon:1, tue:2, wed:3, thu:4, fri:5, sat:6 };
      const s = new Set();
      for (const item of list) {
        const key = String(item).trim().toLowerCase();
        if (key in map) s.add(map[key]);
        else {
          const n = parseInt(key, 10);
          if (Number.isInteger(n) && n >= 0 && n <= 6) s.add(n);
        }
      }
      return s;
    }
  }
  